"use client"

import React, { useEffect, useMemo, useState } from "react"
import { useUbStorefrontState } from "@/store/ubStorefrontState"

import { BasketContext, BasketTotalData } from "@/lib/storefront/basket/basket"
import { determineBasketTotalData } from "@/lib/storefront/basket/basketHelper"

import "react-json-view-lite/dist/index.css"

import Link from "next/link"
import { ChevronDownIcon } from "@radix-ui/react-icons"

import { formatPrice } from "@/lib/helper"
import { Button } from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"
import { isCheckoutAddressesHref, resetBeginCheckoutAttempt } from "@/lib/tracking/begin-checkout-attempt"
import { UbBasketMobileBottomSheetStoryblok } from "@/components/storyblok/component-types"

interface BasketMobileBottomSheetClientProps {
  basketContext: BasketContext
  blok: UbBasketMobileBottomSheetStoryblok
  extended_content: React.ReactNode
}

export default function BasketMobileBottomSheetClient(props: BasketMobileBottomSheetClientProps) {
  const isoLocale = props.basketContext.isoLocale
  const isB2B = props.basketContext.isB2B
  const isAnonymous = props.basketContext.isAnonymous

  const [expanded, setExpanded] = useState(false)

  const { basketData, basketLineItems, basketPromotionCodes } = useUbStorefrontState()

  const basketTotalData: BasketTotalData = useMemo(() => {
    return determineBasketTotalData(
      isB2B,
      basketData,
      basketLineItems,
      basketPromotionCodes,
      isAnonymous,
      props.basketContext.isoLocale
    )
  }, [basketData, basketLineItems, basketPromotionCodes, isB2B, isAnonymous, props.basketContext.isoLocale])

  function toggleExpanded() {
    setExpanded(!expanded)
  }

  const checkoutHref = props.blok?.link_target ?? ""

  useEffect(() => {
    if (expanded) {
      document.body.style.overflowY = "hidden"
    } else {
      document.body.style.overflowY = "auto"
    }
  }, [expanded])

  return (
    <>
      {basketData && basketLineItems && basketLineItems.length > 0 && (
        <div className={"relative block sm:hidden"}>
          {expanded && <div className={"fixed inset-0 z-200 bg-black/80"} onClick={() => toggleExpanded()}></div>}
          <div className={cn("fixed inset-x-0 bottom-0 z-250 h-auto border-t border-gray-400")}>
            <div className={"flex size-full flex-col gap-1 rounded-t-lg bg-white"}>
              <div
                onClick={toggleExpanded}
                className={"flex flex-row items-center justify-between gap-2 border-b border-gray-400 px-2 py-1"}
              >
                <div className={"text-sm font-bold"}>{props.blok?.label_summary ?? "Summary"}</div>
                <Button onClick={toggleExpanded} variant={"ghost"} size={"icon"} className={"size-9"}>
                  {" "}
                  <ChevronDownIcon
                    className={cn("size-7 transition-transform duration-150 ease-in-out", {
                      "rotate-180": !expanded,
                    })}
                    aria-hidden
                  />
                </Button>
              </div>

              <div
                className={cn("", {
                  hidden: !expanded,
                })}
              >
                <div>{props.extended_content}</div>
              </div>

              <div className={"flex flex-row items-center justify-between gap-20 px-2 pt-2 pb-6"}>
                <div className={"flex flex-col items-start"}>
                  <p className={"text-xs"}>{props.blok?.label_gross_price ?? "Bruttobetrag"}</p>
                  <span data-testid={"grandTotalGross"} className="text-right text-xl font-bold text-primary">
                    {formatPrice(isoLocale, basketTotalData.grandTotalGross, basketTotalData.currency)}
                  </span>
                </div>

                <Link
                  prefetch={false}
                  className={"w-full cursor-pointer"}
                  href={checkoutHref}
                  onClick={() => {
                    if (isCheckoutAddressesHref(checkoutHref)) {
                      resetBeginCheckoutAttempt()
                    }
                  }}
                >
                  <Button size={"defaultNoGrow"} variant={"default"} className={"w-full"}>
                    {props.blok?.label_checkout ?? ""}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
